import { Component, Input, OnInit, OnDestroy, QueryList, ViewChildren } from "@angular/core";
import { Event, EventRecurringType } from "../../shared/models/event";
import { EventService } from "../../shared/services/event.service";
import { PlayerService } from "../../shared/services/player.service";
import { BehaviorSubject, Observable } from "rxjs";
import { PlaySpaceService } from "../../shared/services/playspace.service";
import { PlaySpace } from "../../shared/models/playSpace";
import { Player } from "../../shared/models/player";
import { StatsService } from "../../shared/services/stats.service";
import { Stats } from "../../shared/models/stats";
import { ActivatedRoute, Router } from "@angular/router";
import { Location } from "@angular/common";
import { LocalizationService } from "app/shared/services/localization.service";
import { PlayerEventInfo, PlayerEventInfoStatus, PlayerEventInfoJersey, TeamMemberType } from "app/shared/models/playerEventInfo";
import { PlayerEventInfoService } from "app/shared/services/playereventinfo.service";
import { DelayedSpinnerService } from "../../shared/services/delayed-spinner.service";
import { debounceTime, mergeMap } from "rxjs/operators";
import { MapAdvancedMarker, MapInfoWindow } from "@angular/google-maps";
import { BsDatepickerConfig } from "ngx-bootstrap/datepicker";
import { TranslateService } from '@ngx-translate/core';
import { environment } from "app/environments/environment";
import { DialogService } from "../../core/dialog/dialog.service";
import { AuthenticatedRealtimeService } from "../../shared/services/authenticated-realtime.service";
import { navigateBackOrFallback } from "../../shared/screen-shell/screen-navigation.utils";

@Component({
    selector: "event-detail",
    templateUrl: "./event-detail.component.html",
    styleUrls: ["../event.component.css", "../match-home/match-overview.css"],
    standalone: false
})
export class EventDetailComponent implements OnInit, OnDestroy {
  @Input() public editMode: boolean = false;

  public event: Event;
  public eventOld: Event;
  public joinError = "";
  public currentPlayer: Player;

  public currentDate: Date;
  public selectedDate: Date;
  public selectedHour: number;
  public selectedMinute: number;

  public autocompletePlayspace: string;
  public locationNameSelected: boolean;
  public typeaheadLocationLoading: boolean;
  public typeaheadLocationNoResults: boolean;
  public locationDataSource: Observable<any>;

  public toggleModified = false;
  public radioRecurringType: EventRecurringType = new EventRecurringType();

  public mapLatitude: number = 44.78;
  public mapLongitude: number = 20.44;
  public zoom: number = 11;
  public markers: PlaySpace[] = Array<PlaySpace>();
  mapOptions: google.maps.MapOptions;
  clickableAdvancedMarkerOptions: google.maps.marker.AdvancedMarkerElementOptions = {
    gmpClickable: true
  };


  public stats: Stats[];
  public showDeleteDialog: boolean = false;

  public loading = false;
  public readonly eventDetailReady$ = new BehaviorSubject<boolean>(false);

  public autocompleteSelectedPlayer: string;
  public typeaheadMngPlayerLoading: boolean;
  public typeaheadMngPlayerNoResults: boolean;
  public managerEmailSelected: boolean;
  public playerDataSource: Observable<any>;

  public playerEventInfoStatus: PlayerEventInfoStatus = new PlayerEventInfoStatus();
  public playerEventInfoJersey: PlayerEventInfoJersey = new PlayerEventInfoJersey();
  public teamMemberType: TeamMemberType = new TeamMemberType();

  public eventForDelete: Event;

  public navigateHref: string = "";

  bsConfig: Partial<BsDatepickerConfig>;

  @ViewChildren(MapInfoWindow) infoWindows: QueryList<MapInfoWindow>;
  private readonly boundAdvancedMarkers = new WeakSet<google.maps.marker.AdvancedMarkerElement>();

  constructor(
    public playerService: PlayerService,
    public eventService: EventService,
    public route: ActivatedRoute,
    public router: Router,
    public statsService: StatsService,
    public localizationService: LocalizationService,
    public location: Location,
    private playSpaceService: PlaySpaceService,
    private playereventinfoService: PlayerEventInfoService,
    private spinner: DelayedSpinnerService,
    private translate: TranslateService,
    private dialogService: DialogService,
    private realtime: AuthenticatedRealtimeService
  ) {
    this.loading = true;
    this.spinner.show();

    this.bsConfig = {
      containerClass: 'theme-dark-blue', // Add a custom class to the datepicker container
    };

    this.locationDataSource = new Observable((observer: any) => {
      observer.next(this.autocompletePlayspace);
    })
    .pipe(
      debounceTime(500),
      mergeMap((token: string) => this.getPlaySpacesAsObservable(token))
    );

    this.playerDataSource = new Observable((observer: any) => {
      observer.next(this.autocompleteSelectedPlayer);
    })
    .pipe(
      debounceTime(500),
      mergeMap((token: string) => this.getPlayersAsObservable(token))
    );
  }

  ngOnInit() {
    window.scroll(0, 0);

    let me = this;

    if (!localStorage.getItem("event")) {
      me.router.navigate(["/event-list"]);
      return;
    }

    me.loading = true;
    this.eventDetailReady$.next(false);
    me.spinner.show();
    me.editMode = JSON.parse(me.route.snapshot.params["editSelectedEvent"]);

    me.event = JSON.parse(localStorage.getItem("event"));
    me.eventOld = JSON.parse(JSON.stringify(me.event));

    let returningFromPlayspaceCreation = JSON.parse(sessionStorage.getItem("returningFromNewPlayspaceForm"));
    if (returningFromPlayspaceCreation) {
      me.event = JSON.parse(localStorage.getItem("savedEventEdit"));

      let newLocation: PlaySpace = JSON.parse(sessionStorage.getItem("newPlayspace"));
      if (newLocation) {
        me.event.playSpace = newLocation;
      }
    }
    localStorage.removeItem("savedEventEdit");
    sessionStorage.removeItem("returningFromNewPlayspaceForm");

    me.loading = true;
    me.spinner.show();

    if (!me.event) {
      me.event = new Event();

      me.event.recurringType = me.radioRecurringType.WEEKLY;
      me.event.viewedByManager = true;
      me.event.openToPublic = false;
    } else {
      if (me.event.id && me.editMode) {
        me.statsService.getEventStats(me.event.originalEventId).subscribe(
          (stats) => {
            me.stats = stats;
          },
          (error) => {
            console.log(error);
          }
        );
      }
    }

    me.currentPlayer = me.playerService.getCurrentPlayer();
    if (me.currentPlayer && me.event.manager && me.currentPlayer.id === me.event.manager.id) {
      me.event.viewedByManager = true;
    }

    if (me.event.season1StartDate) {
      me.event.season1StartDate = new Date(me.event.season1StartDate);
      me.event.season2StartDate = new Date(me.event.season2StartDate);
    }

    me.event.originalEventId = me.event.originalEventId ? me.event.originalEventId : me.event.id;

    me.event.openToPublic = me.event.openToPublic === true;
    me.autocompletePlayspace = me.event.playSpace ? me.event.playSpace.name : (me.event.locationName || "");
    me.autocompleteSelectedPlayer = me.event.manager ? me.event.manager.name : "";

    me.markers = JSON.parse(localStorage.getItem("nearByLocations")) || [];

    me.markers.forEach(marker => {
      if (this.hasCoordinates(marker)) {
        marker.googleLatLng = new google.maps.LatLng(marker.latitude, marker.longitude);
      }
    });

    if (this.hasCoordinates(me.event.playSpace)) {
      me.mapLatitude = me.event.playSpace.latitude;
      me.mapLongitude = me.event.playSpace.longitude;
      me.event.playSpace.googleLatLng = new google.maps.LatLng(me.event.playSpace.latitude, me.event.playSpace.longitude);

      me.markers.push(me.event.playSpace);
    } else if (this.currentPlayer && this.currentPlayer.location && this.currentPlayer.location.coordinates) {
      me.mapLatitude = me.currentPlayer.location.coordinates[0];
      me.mapLongitude = me.currentPlayer.location.coordinates[1];
    }


    this.mapOptions = {
      mapId: environment.GOOGLE_MAPS_MAP_ID,
      center: {lat: me.mapLatitude, lng: me.mapLongitude},
      zoom: this.zoom
    };

    me.currentDate = new Date();
    const nextOccurrenceValue = me.event.nextOccurrenceTimestamp || (me.event as any).nextOccurrence;
    const nextOccurrenceDate = nextOccurrenceValue ? new Date(nextOccurrenceValue) : null;
    if (nextOccurrenceDate && !isNaN(nextOccurrenceDate.getTime())) {
      me.selectedDate = nextOccurrenceDate;
      me.selectedHour = nextOccurrenceDate.getHours();
      me.selectedMinute = nextOccurrenceDate.getMinutes();
    } else {
      me.selectedDate = null;
      me.selectedHour = 0;
      me.selectedMinute = 0;
    }

    if (me.event.confirmedCount == 0) {
      me.event.confirmedCount = me.eventService.getConfirmedCount(me.event);
    }
    me.openMap();
    me.loading = false;
    me.spinner.hide();
    me.eventDetailReady$.next(true);
    me.focusRequestedSettingsSection();
    this.startPreviewRealtime();
  }

  private stopPreviewRealtime: (() => void) | null = null;
  private previewRefreshTimer: ReturnType<typeof setTimeout>;
  private previewDestroyed = false;

  private startPreviewRealtime(): void {
    if (this.editMode || !this.event?.id) return;
    const eventId = this.event.id;
    const playerId = this.currentPlayer.id;
    this.stopPreviewRealtime = this.realtime.subscribe(({type, data}) => {
      if (type !== 'connected' && !(data?.eventId === eventId && ['participants.changed', 'event.updated', 'event.deleted'].includes(type))) return;
      clearTimeout(this.previewRefreshTimer);
      this.previewRefreshTimer = setTimeout(() => {
        if (this.loading || this.previewDestroyed) return;
        this.eventService.getRichEvent(eventId).subscribe({
          next: event => {
            if (this.previewDestroyed || this.loading || this.playerService.getCurrentPlayer()?.id !== playerId) return;
            const member = event.access?.member || event.manager?.id === playerId;
            if (member) { this.router.navigate(['/event-players', false], {queryParams: {eventId}}); return; }
            this.event = {...event, viewedByCandidate: !!event.access?.requestPending || event.viewedByCandidate === true || event.participation?.teamMemberType === 'CANDIDATE'};
            if (this.hasPrecisePlaySpaceLocation()) {
              this.mapLatitude = Number(event.playSpace.latitude);
              this.mapLongitude = Number(event.playSpace.longitude);
              this.mapOptions = {...this.mapOptions, center: {lat: this.mapLatitude, lng: this.mapLongitude}};
            }
            this.openMap();
          },
          error: error => {
            if (!this.previewDestroyed && [403,404].includes(error?.status)) {
              this.dialogService.alert({title: this.translate.instant('events.openMatchPreviewMissingTitle'), text: this.translate.instant('events.openMatchPreviewMissingText')});
              this.router.navigate(['/event-list']);
            }
          }
        });
      }, 200);
    });
  }

  ngOnDestroy(): void {
    this.previewDestroyed = true;
    this.stopPreviewRealtime?.();
    clearTimeout(this.previewRefreshTimer);
    this.spinner.hide();
  }

  private focusRequestedSettingsSection(): void {
    if (!this.editMode) {
      sessionStorage.removeItem("eventDetailFocusSection");
      return;
    }

    const section = sessionStorage.getItem("eventDetailFocusSection") as "manager" | "seasons" | "maxPlayers" | "rules";
    sessionStorage.removeItem("eventDetailFocusSection");
    const sectionElementIds: Record<"manager" | "seasons" | "maxPlayers" | "rules", string> = {
      manager: "eventManagerSettingsSection",
      seasons: "eventSeasonsSettingsSection",
      maxPlayers: "eventMaxPlayersSettingsSection",
      rules: "eventRulesSettingsSection"
    };
    const targetId = sectionElementIds[section];
    if (!targetId) {
      return;
    }

    setTimeout(() => {
      const target = document.getElementById(targetId);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
        const focusable = target.querySelector("input, textarea") as HTMLElement;
        if (focusable) {
          focusable.focus();
        }
      }
    });
  }

  public openMap(): void {
    if (!this.hasPrecisePlaySpaceLocation()) {
      this.navigateHref = "";
      return;
    }

    if (navigator.platform.indexOf("iPhone") != -1 || navigator.platform.indexOf("iPod") != -1 || navigator.platform.indexOf("iPad") != -1) {
      this.navigateHref = "http://maps.apple.com/?q=" + this.event.playSpace.latitude + "," + this.event.playSpace.longitude;
    } else {
      this.navigateHref = "https://maps.google.com/maps?daddr=" + this.event.playSpace.latitude + "," + this.event.playSpace.longitude;
    }
  }

  public saveEventState(): void {
    localStorage.setItem("savedEventEdit", JSON.stringify(this.event));
  }

  public editEvent(): void {
    this.editMode = true;
  }

  public setEventForDelete(event: Event): void {
    this.eventForDelete = event;
  }

  public removeEvent(): void {
    this.loading = true;
    this.spinner.show();

    this.eventService.removeAllEventOccurrenceByOriginalId(this.eventForDelete.id).subscribe(
      () => {
        this.router.navigate(["/event-list"]);
        this.loading = false;
        this.spinner.hide();
      },
      (res) => {
        this.router.navigate(["/event-list"]);
        console.log(res.error);
        this.loading = false;
        this.spinner.hide();
      }
    );
  }

  openInfoWindow(marker: MapAdvancedMarker, markerNo: number) {
    this.infoWindows.get(markerNo).open(marker);
  }

  bindAdvancedMarkerClick(marker: MapAdvancedMarker, advancedMarker: google.maps.marker.AdvancedMarkerElement, markerNo: number): void {
    if (this.boundAdvancedMarkers.has(advancedMarker)) {
      return;
    }

    this.boundAdvancedMarkers.add(advancedMarker);
    advancedMarker.addEventListener("gmp-click", () => this.openInfoWindow(marker, markerNo));
  }

  public choosePlayLocation(playSpace: PlaySpace): void {
    this.locationNameSelected = true;
    this.event.playSpace = playSpace;
    this.autocompletePlayspace = playSpace.name;
  }

  public onSubmit(viewedByManager: boolean): void {
    let me = this;

    if (!viewedByManager) {
      for (let playerEventInfo of me.event.playerEventInfos) {
        if (me.currentPlayer.id === playerEventInfo.player.id) {
          me.loading = true;
          me.spinner.show();
          break;
        }
      }

      return;
    }

    me.toggleModified = false;

    if (me.selectedDate) {
      me.event.nextOccurrenceTimestamp = new Date(me.selectedDate);
      me.event.nextOccurrenceTimestamp.setHours(me.selectedHour || 0);
      me.event.nextOccurrenceTimestamp.setMinutes(me.selectedMinute || 0);
      me.event.nextOccurrenceTimestamp.setSeconds(0);
      me.event.nextOccurrenceTimestampZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } else {
      me.event.nextOccurrenceTimestamp = null;
      me.event.nextOccurrenceTimestampZone = null;
    }

    me.event.locationId = me.event.playSpace && me.event.playSpace.id ? me.event.playSpace.id : null;
    me.event.locationName = this.autocompletePlayspace || me.event.locationName || null;
    if (me.event.playSpace) {
      me.event.playSpace.location = null;
    }

    me.loading = true;
    me.spinner.show();

    if (me.event.id == null) {
      me.event.manager = this.currentPlayer;
      me.createEvent();
    } else {
      me.eventService.updateEvent(me.event).subscribe(
        (event) => {
          me.event = event;

          me.event.nextOccurrenceTimestamp = new Date(event.nextOccurrenceTimestamp);
          me.event.nextOccurrenceTimestampZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          me.event.viewedByManager = true;

          localStorage.setItem("event", JSON.stringify(event));
          me.spinner.hide();
          me.loading = false;
        },
        (error) => {
          console.log(error);

          me.dialogService.alert({title: me.translate.instant("common.alert"), text: me.translate.instant("errors.generic")});
          me.spinner.hide();
          me.loading = false;
        }
      );
    }
  }

  private addPlayerToPlayerEventInfosList(player: Player): void {
    let currentPlayerEventInfosList = this.event.playerEventInfos;
    let playerEventInfoFound: boolean = false;
    if (currentPlayerEventInfosList !== undefined) {
      for (let playerEventInfo of currentPlayerEventInfosList) {
        if (player.id === playerEventInfo.player.id) {
          playerEventInfoFound = true;
          break;
        }
      }
    }
    if (!playerEventInfoFound) {
      let newPlayerEventInfo: PlayerEventInfo = new PlayerEventInfo();
      newPlayerEventInfo.eventId = this.event.id;
      newPlayerEventInfo.player = player;
      newPlayerEventInfo.jersey = this.playerEventInfoJersey.WHITE;
      newPlayerEventInfo.status = this.playerEventInfoStatus.CONFIRMED;
      newPlayerEventInfo.teamMemberType = this.teamMemberType.RESERVE;

      if (newPlayerEventInfo.eventId) {
        this.playereventinfoService.create(newPlayerEventInfo, this.currentPlayer).subscribe(
          (playerEventInfo) => {
            this.event.playerEventInfos = this.event.playerEventInfos || [];
            this.event.playerEventInfos.push(playerEventInfo);
          },
          (error) => {
            console.log(error);
          }
        );
      } else {
        this.event.playerEventInfos.push(newPlayerEventInfo);
      }
    }
  }

  public changeTypeaheadLoading(e: boolean, fieldName: string): void {
    switch (fieldName) {
      case "locationName":
        this.locationNameSelected = false;
        this.typeaheadLocationLoading = e;
        break;
      case "managerName":
        this.managerEmailSelected = false;
        this.typeaheadMngPlayerLoading = e;
        break;
    }
  }

  public changeTypeaheadNoResults(e: boolean, fieldName: string): void {
    switch (fieldName) {
      case "locationName":
        this.locationNameSelected = false;
        this.typeaheadLocationNoResults = e;
        break;
      case "managerName":
        this.managerEmailSelected = false;
        this.typeaheadMngPlayerNoResults = e;
        break;
    }
  }

  public typeaheadManagerEmailSelected(selectedObject: any): void {
    this.managerEmailSelected = true;
    this.event.manager = selectedObject.item;
  }

  public typeaheadLocationNameSelected(selectedObject: any): void {
    this.locationNameSelected = true;
    this.event.playSpace = selectedObject.item;

    if (this.hasCoordinates(this.event.playSpace)) {
      this.mapLatitude = this.event.playSpace.latitude;
      this.mapLongitude = this.event.playSpace.longitude;
      this.event.playSpace.googleLatLng = new google.maps.LatLng(this.event.playSpace.latitude, this.event.playSpace.longitude);

      this.markers.push(this.event.playSpace);
    }
  }

  public checkIfLocationNameIsSelected(): void {
    if (!this.locationNameSelected) {
      this.event.playSpace = null;
      this.autocompletePlayspace = (this.autocompletePlayspace || "").trim() || null;
      this.event.locationName = this.autocompletePlayspace;
    }
  }

  public cancelEventDetailsChange(): void {
    let me: any = this;
    me.event = JSON.parse(JSON.stringify(me.eventOld));

    me.editMode = false;
    if (this.navigateToReturnRouteIfPresent()) {
      return;
    }
    if (!me.event.id) {
      me.router.navigate(["/event-list"]);
    }
  }

  private navigateToReturnRouteIfPresent(): boolean {
    const returnRoute = sessionStorage.getItem("eventDetailReturnRoute");
    if (!returnRoute) {
      return false;
    }

    sessionStorage.removeItem("eventDetailReturnRoute");
    this.router.navigateByUrl(returnRoute);
    return true;
  }

  public getPlayersAsObservable(partialPlayerNickName: string): Observable<any> {
    return this.playerService.getPlayers(partialPlayerNickName);
  }

  private getPlaySpacesAsObservable(partialLocationName: string): Observable<any> {
    return this.playSpaceService.getPlaySpaces(partialLocationName);
  }

  private createEvent(): void {
    this.eventService.createEvent(this.event).subscribe(
      (event) => {
        this.event = event;
        this.event.playerEventInfos = this.event.playerEventInfos || [];
        this.event.nextOccurrenceTimestamp = event.nextOccurrenceTimestamp ? new Date(event.nextOccurrenceTimestamp) : null;
        this.event.viewedByManager = true;
        this.addPlayerToPlayerEventInfosList(this.currentPlayer);

        localStorage.setItem("event", JSON.stringify(event));
        this.loading = false;
        this.spinner.hide();
        this.router.navigate(["/event-players", false]);
      },
      (error) => {
        console.log(error);
        this.spinner.hide();
        this.loading = false;
      }
    );
  }

  public goBack(): void {
    navigateBackOrFallback(this.location, this.router, ["/event-list"]);
  }

  public eventDetailTitle(): string {
    if (!this.event) {
      return this.translate.instant("events.match");
    }
    const prefix = !this.event.id
      ? `${this.translate.instant("events.create")} `
      : (this.editMode ? `${this.translate.instant("common.edit")} ` : "");
    const suffix = this.event.id && !this.editMode ? ` ${this.translate.instant("common.details")}` : "";
    return `${prefix}${this.event.name || ""}${suffix}`.trim();
  }

  public canRequestJoin(): boolean {
    const event = this.event;
    return !!event && event.active !== false && event.openToPublic && !event.cancelled && !event.incompleteSetup &&
      !event.viewedByCandidate && !event.viewedByPlayer && !event.viewedByManager && event.access?.canRequestJoin !== false;
  }

  public withdrawRequest(): void {
    if (!this.event.viewedByCandidate || this.loading || this.event.access?.canLeave === false) return;
    this.dialogService.alert({ title: this.translate.instant('catchUp.withdrawRequest'), text: this.translate.instant('catchUp.withdrawDescription'), closeLabel: this.translate.instant('common.cancel'), actionLabel: this.translate.instant('catchUp.withdrawRequest'), action: () => {
      this.loading = true;
      this.spinner.show();
      this.eventService.requestToLeaveEvent(this.event, this.currentPlayer).subscribe(() => {
        this.spinner.hide(); this.loading = false; this.router.navigate(['/event-list']);
      }, () => { this.spinner.hide(); this.loading = false; this.joinError = this.translate.instant('errors.generic'); });
    }});
  }

  public previewReliability(): string {
    const activity = this.event.activitySummary;
    if (!activity?.recentMatchCount) return this.translate.instant('events.noPlayedMatches');
    return this.translate.instant('events.openMatchPlayedRecent', {played: activity.playedMatchCount || 0, total: activity.recentMatchCount});
  }

  public previewRecurringKey(): string {
    return ({ DAILY: 'events.daily', WEEKLY: 'events.weekly', MONTHLY: 'events.occurrenceMonthly', ONE_OFF: 'events.occurrenceOneOff' })[this.event.recurringType] || 'events.occurrenceOneOff';
  }

  public requestToJoin(event: Event): void {
    if (this.loading || !this.canRequestJoin()) return;
    this.joinError = "";
    this.loading = true;
    this.spinner.show();
    this.eventService.requestToJoinEvent(event, this.currentPlayer).subscribe(
      () => {
        this.dialogService.alert({
          title: this.translate.instant('common.alert'),
          text: this.translate.instant('errors.joinRequestSent')
        });
        this.event.viewedByCandidate = true;
        localStorage.setItem("event", JSON.stringify(this.event));
        this.spinner.hide();
        this.loading = false;
      },
      () => {
        this.joinError = this.translate.instant('errors.generic');
        this.spinner.hide();
        this.loading = false;
      }
    );
  }

  public hasPrecisePlaySpaceLocation(): boolean {
    return this.hasCoordinates(this.event && this.event.playSpace);
  }

  public eventLocationLabel(): string {
    if (this.event && this.event.playSpace && this.event.playSpace.name) {
      return this.event.playSpace.name;
    }

    return this.event && this.event.locationName ? this.event.locationName : this.translate.instant('events.locationTbd');
  }

  public eventTimeLabel(): string {
    return this.event && this.event.nextOccurrenceTimestamp ? "" : this.translate.instant('events.timeNotSet');
  }

  public visibilitySuggestion(): string {
    if (this.event && this.event.openToPublic === true && !this.hasPrecisePlaySpaceLocation()) {
      return this.translate.instant('events.openMatchLocationSuggestion');
    }

    return "";
  }

  private hasCoordinates(playSpace: PlaySpace): boolean {
    return !!playSpace && playSpace.latitude !== null && playSpace.latitude !== undefined &&
      playSpace.longitude !== null && playSpace.longitude !== undefined &&
      isFinite(Number(playSpace.latitude)) && isFinite(Number(playSpace.longitude));
  }
}
